import { Variants } from 'framer-motion';

// Delta-based step definitions
interface StepDelta {
  rotation?: number;
  arrow?: { opacity: number };
  olliePosition?: { y: number; opacity: number };
  ollieTextVisible?: boolean;
  arcs?: {
    north?: Record<string, unknown>;
    east?: Record<string, unknown>;
    eastTop?: Record<string, unknown>;
    eastBottom?: Record<string, unknown>;
    south?: Record<string, unknown>;
    west?: Record<string, unknown>;
  };
  elements?: {
    north?: Record<string, unknown>;
    east?: Record<string, unknown>;
    south?: Record<string, unknown>;
    west?: Record<string, unknown>;
  };
}

// Base states
const baseState = {
  rotation: 0,
  arrow: { opacity: 1 },
  olliePosition: { y: 0, opacity: 0 },
  ollieTextVisible: false,
  arcs: {
    north: { strokeWidth: 2, strokeDasharray: '4 8', stroke: '#EEEBEE' },
    east: { strokeWidth: 2, strokeDasharray: '4 8', stroke: '#EEEBEE' },
    eastTop: { strokeWidth: 2, strokeDasharray: '4 8', stroke: '#EEEBEE' },
    eastBottom: { strokeWidth: 2, strokeDasharray: '4 8', stroke: '#EEEBEE' },
    south: { strokeWidth: 2, strokeDasharray: '4 8', stroke: '#EEEBEE' },
    west: { strokeWidth: 2, strokeDasharray: '4 8', stroke: '#EEEBEE' },
  },
  elements: {
    north: { scale: 1, color: '#18010C' },
    east: { scale: 1, color: '#EEEBEE' },
    south: { scale: 1, color: '#EEEBEE' },
    west: { scale: 1, color: '#EEEBEE' },
  },
};

// Step deltas - only what changes
const stepDeltas: StepDelta[] = [
  {}, // Step 0: initial state
  {
    rotation: -90,
    arcs: { north: { strokeDasharray: '6 6', stroke: '#18010C' } },
    elements: { east: { color: '#18010C' } },
  },
  {
    rotation: -180,
    arcs: {
      east: { strokeDasharray: '6 6', stroke: '#18010C' },
      eastTop: { strokeDasharray: '6 6', stroke: '#18010C' },
      eastBottom: { strokeDasharray: '6 6', stroke: '#18010C' },
    },
    elements: { south: { color: '#18010C' } },
  },
  {
    rotation: -270,
    arcs: { south: { strokeDasharray: '6 6', stroke: '#18010C' } },
    elements: { west: { color: '#18010C' } },
  },
  {
    rotation: -360,
    arcs: { west: { strokeDasharray: '6 6', stroke: '#18010C' } },
    elements: { north: { scale: 1.2 } },
  },
  {
    rotation: -450,
    arcs: { north: { strokeDasharray: 'none' } },
    elements: { east: { scale: 1.2 } },
  },
  {
    rotation: -540,
    arcs: {
      east: { strokeDasharray: 'none' },
      eastTop: { strokeDasharray: 'none' },
      eastBottom: { strokeDasharray: 'none' },
    },
    elements: { south: { scale: 1.2 } },
  },
  {
    rotation: -630,
    arcs: { south: { strokeDasharray: 'none' } },
    elements: { west: { scale: 1.2 } },
  },
  {
    rotation: -720,
    arcs: { west: { strokeDasharray: 'none' } },
    elements: { north: { scale: 1.2 } },
  },
  {
    rotation: -810,
    arrow: { opacity: 0 },
    olliePosition: { y: -30, opacity: 1 },
    ollieTextVisible: false,
  },
  {
    rotation: -855,
    olliePosition: { y: -75, opacity: 1 },
    ollieTextVisible: true,
    arcs: {
      east: { stroke: '#EEEBEE' },
      eastTop: { stroke: '#18010C' },
      eastBottom: { stroke: '#EEEBEE' },
      south: { stroke: '#EEEBEE' },
      west: { stroke: '#EEEBEE' },
    },
    elements: {
      north: { color: '#18010C' },
      east: { color: '#18010C' },
      south: { color: '#EEEBEE' },
      west: { color: '#EEEBEE' },
    },
  },
];

// Build complete variants from deltas
const buildVariants = () => {
  const containerVariants: Variants = {};
  const arcVariants: Record<string, Variants> = {
    north: {},
    east: {},
    eastTop: {},
    eastBottom: {},
    south: {},
    west: {},
  };
  const elementVariants: Record<string, Variants> = {
    north: {},
    east: {},
    south: {},
    west: {},
  };
  const ollieVariants: Variants = {};
  const ollieTextVariants: Variants = {};
  const arrowVariants: Variants = {};
  // Build each step by accumulating deltas
  for (let step = 0; step < stepDeltas.length; step++) {
    let currentState = JSON.parse(JSON.stringify(baseState));

    // Apply all deltas up to current step
    for (let i = 1; i <= step; i++) {
      const delta = stepDeltas[i];
      if (delta.rotation !== undefined) currentState.rotation = delta.rotation;
      if (delta.arrow !== undefined) currentState.arrow = delta.arrow;
      if (delta.olliePosition !== undefined) currentState.olliePosition = delta.olliePosition;
      if (delta.ollieTextVisible !== undefined) currentState.ollieTextVisible = delta.ollieTextVisible;
      if (delta.arcs) {
        Object.entries(delta.arcs).forEach(([pos, changes]) => {
          currentState.arcs[pos as keyof typeof currentState.arcs] = {
            ...currentState.arcs[pos as keyof typeof currentState.arcs],
            ...changes,
          };
        });
      }
      if (delta.elements) {
        Object.entries(delta.elements).forEach(([pos, changes]) => {
          currentState.elements[pos as keyof typeof currentState.elements] = {
            ...currentState.elements[pos as keyof typeof currentState.elements],
            ...changes,
          };
        });
      }
    }

    const stepKey = `step${step}`;

    // Container variants
    containerVariants[stepKey] = {
      rotate: currentState.rotation,
      transition: {
        type: 'spring',
        stiffness: 120,
        damping: 14,
        mass: 1,
      },
    };

    // Arc variants
    (['north', 'east', 'eastTop', 'eastBottom', 'south', 'west'] as const).forEach((pos) => {
      arcVariants[pos][stepKey] = {
        ...currentState.arcs[pos],
        transition: { duration: 0.3, ease: 'easeInOut' },
      };
    });

    // Element variants
    (['north', 'east', 'south', 'west'] as const).forEach((pos) => {
      elementVariants[pos][stepKey] = {
        ...currentState.elements[pos],
        transition: { duration: 0.4, ease: 'easeOut', delay: 0.2 },
      };
    });

    // Ollie variants
    ollieVariants[stepKey] = {
      y: currentState.olliePosition.y,
      opacity: currentState.olliePosition.opacity,
      transition: {
        y: {
          type: 'spring',
          stiffness: 100,
          damping: 15,
          mass: 1,
        },
        opacity: {
          duration: 0.3,
          ease: 'easeOut',
        },
      },
    };

    // Ollie text variants
    ollieTextVariants[stepKey] = {
      opacity: currentState.ollieTextVisible ? 1 : 0,
      transition: { duration: 0.5 },
    };

    // Arrow variants
    arrowVariants[stepKey] = {
      opacity: currentState.arrow.opacity,
      transition: { duration: 0.3, ease: 'easeInOut' },
    };
  }

  return { containerVariants, arcVariants, elementVariants, ollieVariants, ollieTextVariants, arrowVariants };
};

// Generate the variants
const { containerVariants, arcVariants, elementVariants, ollieVariants, ollieTextVariants, arrowVariants } = buildVariants();

// Export the generated variants
export { containerVariants, arcVariants, elementVariants, ollieVariants, ollieTextVariants, arrowVariants };
